import { Heading } from 'src/components/Heading';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { useState, useEffect, useCallback } from 'react';
import { SyncSummaryModal } from './components/FieldMapping/SyncSummaryModal';
import { SyncSummary } from './types/sync.types';
import { Input } from 'src/components/Input';
import Table, { Header, Row } from 'src/components/Table';
import { MagnifyingGlass, Pencil, CaretDown } from '@phosphor-icons/react';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { ContentWrapper } from 'src/components/ContentWrapper';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  PaginatedResponse,
  WorkflowExecutionThreadFindAllFilters,
  WorkflowExecutionThreadWithComputedStatus,
} from 'src/interfaces/workflows.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { extractDigits } from 'src/utils/number';
import { useCampaigns } from 'src/hooks/useCampaigns';

enum ContactImportType {
  CSV = 'csv',
  CRM = 'crm',
}

// Contact interface based on workflow thread data
interface Contact {
  id: string;
  toNumber: string;
  contactName: string;
  source: string;
  dateAdded: string;
}

const ContactList = () => {
  const { id: campaignReferenceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { get } = useAuthedRequest();
  const campaignsService = useCampaigns();
  const [showSyncSummary, setShowSyncSummary] = useState(false);
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);

  // Campaign loading state
  const [campaignExists, setCampaignExists] = useState<boolean | null>(null);
  const [isCampaignLoading, setIsCampaignLoading] = useState(false);

  // Table state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [isAddContactsDropdownOpen, setIsAddContactsDropdownOpen] =
    useState(false);

  // Table headers
  const headers: Header[] = [
    { key: 'toNumber', label: 'To number', width: '25%', disableSorting: true },
    {
      key: 'contactName',
      label: 'Contact name',
      width: '25%',
      disableSorting: true,
    },
    { key: 'source', label: 'Source', width: '25%', disableSorting: true },
    {
      key: 'dateAdded',
      label: 'Date added',
      width: '25%',
      disableSorting: true,
    },
  ];

  // Check for sync summary data from navigation state
  useEffect(() => {
    if (location.state?.syncSummary) {
      setSyncSummary(location.state.syncSummary);
      setShowSyncSummary(true);

      // Clear the state to prevent showing modal again on refresh
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  // Validate campaign exists
  useEffect(() => {
    const validateCampaign = async () => {
      if (!campaignReferenceId || campaignExists) return;

      setIsCampaignLoading(true);
      try {
        await campaignsService.fetchCampaign(campaignReferenceId);
        setCampaignExists(true);
      } catch {
        navigate('/campaigns', {
          state: { error: 'Campaign not found' },
        });
        return;
      } finally {
        setIsCampaignLoading(false);
      }
    };

    validateCampaign();
  }, [campaignReferenceId, campaignExists, campaignsService, navigate]);

  // Function to fetch contacts from workflow_executions
  const fetchContacts = useCallback(async () => {
    if (!campaignReferenceId || !campaignExists) return;

    setLoading(true);
    try {
      const queryFilters: WorkflowExecutionThreadFindAllFilters = {
        workflowReferenceId: campaignReferenceId,
      };

      // Add search filter (search by phone number)
      const numberDigitsOnly = extractDigits(searchQuery || '');
      if (numberDigitsOnly.length > 0) {
        queryFilters.executionParams = {
          to_number: numberDigitsOnly,
        };
      }

      const queryObject: QueryObject = {
        page: String(currentPage),
        size: String(currentPageSize),
        filters: JSON.stringify(queryFilters),
        sort_column: 'thread_created_at',
        sort_desc: 'true',
      };

      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
        await get(`/workflow_threads?${query.toString()}`);

      // Map workflow thread data to Contact interface
      const mappedContacts: Contact[] = data.items.map(
        (thread: WorkflowExecutionThreadWithComputedStatus) => {
          // TODO: Get source from source_record
          const source = '—';

          return {
            id: thread.id,
            toNumber: (thread.execution_params?.to_number as string) || 'N/A',
            contactName:
              (thread.execution_params?.contact_name as string) || '',
            source,
            dateAdded: new Date(thread.created_at).toLocaleDateString(),
          };
        },
      );

      setContacts(mappedContacts);
      setTotalItems(data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    campaignExists,
    campaignReferenceId,
    currentPage,
    currentPageSize,
    get,
    searchQuery,
  ]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = () => {
      setIsAddContactsDropdownOpen(false);
    };

    if (isAddContactsDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isAddContactsDropdownOpen]);

  if (isCampaignLoading) {
    return (
      <div className="flex gap-2 items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <p className="text-neutral-400">Loading campaign...</p>
      </div>
    );
  }

  const rows: Row[] = contacts.map((contact) => ({
    toNumber: contact.toNumber,
    contactName: contact.contactName,
    source: contact.source,
    dateAdded: contact.dateAdded,
  }));

  const handleAddContactsDropdown = () => {
    setIsAddContactsDropdownOpen((prev) => !prev);
  };

  const handleAddContactsFromCrm = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams({ returnTo });
    navigate(
      `/campaigns/${campaignReferenceId}/contacts/crm?${params.toString()}`,
    );
  };

  const handleAddContactsFromCsv = () => {
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams({ returnTo });
    navigate(
      `/campaigns/${campaignReferenceId}/contacts/csv?${params.toString()}`,
    );
  };

  const handleEditCampaign = () => {
    navigate(`/campaigns/edit/${campaignReferenceId}`);
  };

  const handleDropdownItemClick = (item: string) => {
    if (item === ContactImportType.CSV) {
      handleAddContactsFromCsv();
    } else if (item === ContactImportType.CRM) {
      handleAddContactsFromCrm();
    }
  };

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  return (
    <>
      <Heading
        title="Contact List"
        subtitle="Manage contacts in your campaign"
        backAction={() => navigate('/campaigns')}
      >
        <div className="flex gap-2">
          <Button variant="outlined" onClick={handleEditCampaign}>
            <Pencil size={16} className="mr-2" />
            Edit Campaign
          </Button>
          <div className="relative">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleAddContactsDropdown();
              }}
            >
              Add contacts
              <CaretDown size={16} className="ml-2" />
            </Button>
            <DropdownMenu
              isOpen={isAddContactsDropdownOpen}
              position="bottom-right"
              onItemClick={handleDropdownItemClick}
              className="mr-[-12px]"
            >
              <li
                className="px-4 py-3 hover:bg-neutral-50 cursor-pointer"
                onClick={() => handleDropdownItemClick(ContactImportType.CSV)}
              >
                <div>
                  <div className="font-medium text-neutral-700">
                    Upload a CSV
                  </div>
                  <div className="text-sm text-neutral-500">
                    Add contacts using a CSV file
                  </div>
                </div>
              </li>
              <li
                className="px-4 py-3 hover:bg-neutral-50 cursor-pointer"
                onClick={() => handleDropdownItemClick(ContactImportType.CRM)}
              >
                <div>
                  <div className="font-medium text-neutral-700">
                    Sync from CRM
                  </div>
                  <div className="text-sm text-neutral-500">
                    Import contacts directly from your CRM system
                  </div>
                </div>
              </li>
            </DropdownMenu>
          </div>
        </div>
      </Heading>

      <ContentWrapper>
        {/* Search */}
        <div className="flex mb-6">
          <div className="w-full md:w-96">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by phone number"
              icon={<MagnifyingGlass size={20} className="text-neutral-400" />}
              iconPosition="left"
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  fetchContacts();
                }
              }}
              inputAction={() => fetchContacts()}
            />
          </div>
        </div>

        {/* Table */}
        {contacts.length === 0 && !loading ? (
          <div className="text-center py-12">
            <p className="text-neutral-500 text-lg">
              Add contacts to your campaign
            </p>
          </div>
        ) : (
          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={currentPageSize}
            onSort={() => {}}
            disablePagination={totalItems <= currentPageSize}
          />
        )}
      </ContentWrapper>

      {/* Sync Summary Modal */}
      {syncSummary && (
        <SyncSummaryModal
          isOpen={showSyncSummary}
          onClose={() => setShowSyncSummary(false)}
          summary={syncSummary}
        />
      )}
    </>
  );
};

export default ContactList;
